import { Button, Card, Spinner, Tabs, TabsContent } from '@1trading/react';
import { ArrowLeft, TriangleAlert } from 'lucide-react';
import { Suspense, useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useConsoleUser } from '@/hooks/queries/users/use-user';
import { useDocumentTitle } from '@/hooks/use-document-title';
import { useMediaQuery } from '@/hooks/use-media-query';
import { getErrorMessage } from '@/lib/api/error';
import type { ConsoleUser } from '@/services/api/user-console';
import { SegmentDialog } from './segment-dialog';
import { DEFAULT_TAB, groupTabs, resolveTabParam, visibleTabsFor } from './tab-registry';
import { UserHeader } from './user-header';
import { UserSectionNav } from './user-section-nav';

const USER_SEARCH_ROUTE = '/operations/user-search';
const RAIL_MEDIA_QUERY = '(min-width: 1280px)';
const DOCUMENT_TITLE_SUFFIX = 'User - Bespoke Ops Portal - One Trading';

interface NormalizationState {
  normalizedFrom: string;
}

const isNormalizedArrival = (state: unknown): boolean => typeof state === 'object' && state !== null && 'normalizedFrom' in state;

const CenteredSpinner = () => (
  <div className="flex min-h-[50vh] w-full items-center justify-center">
    <Spinner className="size-8" />
  </div>
);
CenteredSpinner.displayName = 'CenteredSpinner';

interface ErrorPanelProps {
  message: string;
  onRetry?: () => void;
}

// FIX: legacy either crashed in the template or sat blank forever on a
// failed/404 user fetch — render a visible error panel with retry instead
const ErrorPanel = ({ message, onRetry }: ErrorPanelProps) => (
  <Card className="flex w-full max-w-xl flex-col items-start gap-3 p-6">
    <div className="flex items-center gap-2 text-red-500">
      <TriangleAlert className="size-5" />
      <h2 className="text-lg font-semibold">Could not load user</h2>
    </div>
    <p className="text-sm text-muted-foreground">{message}</p>
    {onRetry ? (
      <Button onClick={onRetry} size="sm" variant="outline">
        Retry
      </Button>
    ) : null}
  </Card>
);
ErrorPanel.displayName = 'ErrorPanel';

interface UserConsoleProps {
  user: ConsoleUser;
}

// Rendered only once the user query resolved under the canonical PID — every child
// keys off user.PID, never the raw route param
const UserConsole = ({ user }: UserConsoleProps) => {
  const pid = user.PID;
  const [searchParams, setSearchParams] = useSearchParams();
  const [isSegmentDialogOpen, setIsSegmentDialogOpen] = useState(false);

  const visibleTabs = useMemo(() => visibleTabsFor(user.userType), [user.userType]);
  const visibleValues = useMemo(() => visibleTabs.map((tab) => tab.value), [visibleTabs]);
  const tabGroups = useMemo(() => groupTabs(visibleTabs), [visibleTabs]);
  const isRail = useMediaQuery(RAIL_MEDIA_QUERY);

  // ?tab= is the single source of truth; hidden/unknown values (incl. the legacy
  // 'verification' alias) resolve to the default tab
  const activeTab = resolveTabParam(searchParams.get('tab'), visibleValues);

  // Lazy per-tab mounting: a panel mounts on first activation and then stays mounted
  // (hidden via CSS) so tab switches never lose in-progress form state
  const [activatedTabs, setActivatedTabs] = useState<ReadonlySet<string>>(() => new Set([activeTab]));
  useEffect(() => {
    setActivatedTabs((previous) => (previous.has(activeTab) ? previous : new Set([...previous, activeTab])));
  }, [activeTab]);

  const handleTabChange = (value: string) => {
    // FIX: persist ?tab= for EVERY tab (replace-style, other params kept);
    // the default tab drops the param to keep URLs clean
    setSearchParams(
      (previous) => {
        const next = new URLSearchParams(previous);
        if (value === DEFAULT_TAB) {
          next.delete('tab');
        } else {
          next.set('tab', value);
        }
        return next;
      },
      { replace: true },
    );
  };

  return (
    <>
      <UserHeader onEditSegment={() => setIsSegmentDialogOpen(true)} onManageAccess={() => handleTabChange('access')} user={user} />
      <SegmentDialog currentSegment={user.segment} onOpenChange={setIsSegmentDialogOpen} open={isSegmentDialogOpen} pid={pid} userState={user.state} />
      <Tabs
        className="flex w-full flex-col gap-6 xl:flex-row xl:items-start xl:gap-10"
        onValueChange={handleTabChange}
        orientation={isRail ? 'vertical' : 'horizontal'}
        value={activeTab}
      >
        <UserSectionNav activeTab={activeTab} groups={tabGroups} isRail={isRail} />
        <div className="min-w-0 flex-1">
          {visibleTabs
            .filter((tab) => activatedTabs.has(tab.value))
            .map(({ value, Component, sensitive }) => (
              // Sensitive tabs (PII/KYC) unmount when hidden so their payloads leave
              // memory on tab switch; all other tabs stay mounted to keep their state
              <TabsContent className="mt-0 data-[state=inactive]:hidden" forceMount={sensitive ? undefined : true} key={value} value={value}>
                <Suspense fallback={<CenteredSpinner />}>
                  <Component pid={pid} user={user} />
                </Suspense>
              </TabsContent>
            ))}
        </div>
      </Tabs>
    </>
  );
};
UserConsole.displayName = 'UserConsole';

interface UserPageProps {
  /** raw route param — may be a PID, fast id, or email */
  pid: string;
}

export const UserPage = ({ pid: routePid }: UserPageProps) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { data: user, isPending, isError, error, refetch } = useConsoleUser(routePid);

  useDocumentTitle(user ? `${user.name ?? user.email ?? user.PID} · ${DOCUMENT_TITLE_SUFFIX}` : DOCUMENT_TITLE_SUFFIX);

  // Fast ids, emails and subaccount PIDs all resolve to the account holder's PID
  const needsNormalization = user !== undefined && user.PID !== routePid;
  // FIX: legacy fetchData() re-entered itself after the redirect and could
  // loop forever — allow one redirect, and error if its target STILL resolves differently
  const isNormalizationLoop = needsNormalization && isNormalizedArrival(location.state);

  useEffect(() => {
    if (!needsNormalization || isNormalizationLoop || !user) return;
    const state: NormalizationState = { normalizedFrom: routePid };
    // Preserve the query string so ?tab= deep links survive normalization
    navigate({ pathname: `/operations/users/${user.PID}`, search: searchParams.toString() }, { replace: true, state });
  }, [needsNormalization, isNormalizationLoop, user, routePid, navigate, searchParams]);

  const renderBody = () => {
    if (isPending) return <CenteredSpinner />;
    if (isError) return <ErrorPanel message={getErrorMessage(error)} onRetry={() => refetch()} />;
    if (isNormalizationLoop) return <ErrorPanel message={`bop-api resolved this user to a different PID (${user.PID}) — cannot normalize the URL.`} />;
    // Redirecting to the canonical PID — the query re-runs under the new key
    if (needsNormalization) return <CenteredSpinner />;
    return <UserConsole key={user.PID} user={user} />;
  };

  return (
    <div className="mx-auto flex w-full max-w-[1400px] flex-col gap-6" data-sticky-page>
      <Link className="inline-flex w-fit items-center gap-1 text-sm text-muted-foreground hover:text-foreground" to={USER_SEARCH_ROUTE}>
        <ArrowLeft className="size-4" />
        Back to user search
      </Link>
      {renderBody()}
    </div>
  );
};
UserPage.displayName = 'UserPage';
